import { RecordModel } from "../core";
import RecordSelect from "../../components/fields/RecordSelect";
import { OwnerModel, UnitModel } from "./housing";
import { Badge, DatePicker, Tag } from "antd";
import { FileProtectOutlined, SendOutlined } from '@ant-design/icons';
import moment from "moment/moment";
import React from "react";
import { PresetStatusColorType } from "antd/lib/_util/colors";
import { UserModel } from "./auth";
import { Link } from "react-router-dom";

export type IJournal = {
  id: number;
  owner_name: string;
  description: string;
  is_main: boolean;
  content_object: any;
}

export const JournalModel: RecordModel<IJournal> = {
  name: "Journal",
  path: "journals",
  selectQuery: "",
  tableQuery: "",
  toString: r => r.owner_name || `Journal ${r.id}`,
}

export type IInvoice = {
  id: number;
  journal: {
    description: string;
    is_main: boolean,
    invoices: number[];
    owner_name: string;
  };
  billing_period: string;
  due_date: string;
  is_paid: boolean;
  status: string;
  total: number;
  stripe_invoice: any;
  invoiceline_set: number[];
  currency: any;
  auto_description: string;
}

const invoiceBadgeStatuses: {[status: string]: PresetStatusColorType} = {
  draft: 'default',
  open: 'processing',
  paid: 'success',
  uncollectible: 'warning',
  void: 'warning',
  overdue: 'error',
}

export const InvoiceModel: RecordModel<IInvoice> = {
  name: "Invoice",
  path: "journal-invoices",
  selectQuery: "",
  tableQuery: "&expand=journal,currency,stripe_invoice",
  toString(r): string {
    return r.journal.owner_name;
  },
  icon: <FileProtectOutlined />,
  customTableMappings: {
    journal: { path: ['journal', 'owner_name'], title: 'Owner', width: 180 },
    auto_description: { width: 170, render: r => <small>{r.auto_description}</small> },
    total: { render: r => `$${(r.total/100).toLocaleString()} ${r.currency.iso}`, width: 135 },
    status: { render: r => <Badge status={invoiceBadgeStatuses[r.status]} text={r.status} />, width: 70 },
    stripe_invoice: {
      title: 'Stripe number',
      render: r => {
        const url = `https://dashboard.stripe.com/acct_1FmmYDDIqmrOjGrM/invoices/${r.stripe_invoice?.stripe_id}`;
        const openPopup = (e: React.MouseEvent) => {
          e.preventDefault();
          const width = 600;
          const height = 750;
          const left = (window.screen.width - width) / 2;
          const top = (window.screen.height - height) / 4;
          window.open(url, 'stripe_invoice', `width=500,height=750,left=${left},top=${top},scrollbars=yes,resizable=yes`);
        };
        return (
          <a href={url} onClick={openPopup} style={{ fontSize: 12, fontWeight: 500, fontFamily: 'monospace' }}>
            {r.stripe_invoice?.number}
          </a>
        );
      }, width: 115
    },
    "is_paid": { omit: true }, currency: { omit: true }, invoiceline_set: { omit: true }
  },
  customCreateFields: [
    { name: 'stripe_invoice', component: null },
  ],
  tableFilters: [
    { selectModel: UnitModel },
    { selectModel: OwnerModel },
    { component: ({ query, handleFilter }) => {
      const MonthPickerComponent = DatePicker.MonthPicker as any;
      return (
        <MonthPickerComponent
          defaultValue={!!query.get('billing_period') ? moment(query.get('billing_period')) : undefined}
          onChange={(v: any) => handleFilter('billing_period', v ? v.startOf('month').format('YYYY-MM-DD') : '')}
        />
      )
    }},
    { selectField: 'status' },
  ],
  defaultValues: {
    due_date: moment().add(7, 'days').format("YYYY-MM-DD"),
  },
  detailAction: '/invoices',
  customActions: [{
    name: 'Send',
    path: 'advance',
    isDetail: true,
    icon: <SendOutlined />,
    disabled: r => r.status !== 'draft'
  }],
  superProtectEdits: true,
}

export interface IUsagePattern {
  id: number;
  created_at: string;
  modified_at: string;
  name: string;
  frequency: "Ad Hoc" | "Monthly" | "Bimonthly";
}

export const UsagePatternModel: RecordModel<IUsagePattern> = {
  name: "Usage Pattern",
  path: "usage-patterns",
  selectQuery: "",
  tableQuery: "",
  toString: r => r.name,
}


export interface IBalanceTransaction {
  id: number;
  stripe_id: string;
  created_at: string;
  net_amount: number;
  currency: "mxn" | "usd";
  ending_balance: number;
  type: "funded" | "applied_to_payment";
  customer: number;
  invoice: number | null;
  payment_intent: string | null;
}


const TransactionAmount = ({ amount, currency, isBalance }: { amount: number, currency: string, isBalance?: boolean }) => {
  return (
    <div className="full-width right-text fw-500" style={!isBalance ? { color: amount >= 0 ? '#389e0d' : '#d4380d' } : {}}>
      {isBalance ? '' : amount >= 0 ? '+' : '-'}${Math.abs(amount/100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency.toUpperCase()}
    </div>
  )
}

export const BalanceTransactionModel: RecordModel<IBalanceTransaction> = {
  name: "Balance Transaction",
  path: "cash-balance-transactions",
  selectQuery: "",
  tableQuery: "",
  toString: r => r.stripe_id,
  customTableMappings: {
    ...(Object.fromEntries(['customer', 'currency'].map(fieldName => [fieldName, { omit: true }]))),
    net_amount: { title: 'Amount', align: 'right', render: r => <TransactionAmount amount={r.net_amount} currency={r.currency} /> },
    ending_balance: { title: 'Balance', align: 'right', render: r => <TransactionAmount amount={r.ending_balance} currency={r.currency} isBalance /> },
    created_at: { title: 'Date' },
    invoice: { render: r => r.invoice ? <Link to={`/invoices/${r.invoice}`}>{r.invoice}</Link> : null },
  },
  tableFilters: [
    {
      component: (q, props) => (
        <RecordSelect
          filter="customer"
          recordModel={UserModel}
          extraFilterQuery=",stripe_accounts&expand=stripe_accounts&is_staff=false&has_full_name=true"
          valueMapper={r => (!!r.stripe_accounts && r.stripe_accounts[0]) ? (r.stripe_accounts[0] as any).id.toString() : 0}
        />
      )
    },
  ]
}

export const BalanceTransactionSlimModel: RecordModel<IBalanceTransaction> = {
  name: "Balance Transaction",
  path: "cash-balance-transactions",
  selectQuery: "",
  tableQuery: "&ordering=-created_at",
  toString: r => r.stripe_id,
  customTableMappings: {
    ...(Object.fromEntries(['customer', 'currency', 'type', 'payment_intent', 'invoice'].map(fieldName => [fieldName, { omit: true }]))),
    stripe_id: {
      title: 'Transaction',
      render: r => {
        return (
          <div>
            {<Tag color={r.net_amount >= 0 ? 'green' : 'volcano'}>{r.type.replaceAll("_", " ")}</Tag>}
          </div>
        )
      }
    },
    net_amount: { title: 'Amount', align: 'right', render: r => <TransactionAmount amount={r.net_amount} currency={r.currency} /> },
    ending_balance: { title: 'Balance', align: 'right', render: r => <TransactionAmount amount={r.ending_balance} currency={r.currency} isBalance /> },
    created_at: { title: 'Date', align: 'right' },
  },
  tableFilters: [
    {
      component: (q, props) => (
        <RecordSelect
          filter="customer"
          recordModel={UserModel}
          extraFilterQuery=",stripe_accounts&expand=stripe_accounts&is_staff=false&has_full_name=true"
          valueMapper={r => (!!r.stripe_accounts && r.stripe_accounts[0]) ? (r.stripe_accounts[0] as any).id.toString() : 0}
        />
      )
    },
  ],
  pageSize: 100,
}
